import { useRef, useEffect, MutableRefObject, isValidElement, ReactElement, useState } from 'react';

import Analytics from '@hh.ru/analytics-js';
import priceDbaccessAddContactsTabButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/price/dbaccess/price_dbaccess_add-contacts_tab_button_click';
import priceDbaccessAndAddContactsTabButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/price/dbaccess/price_dbaccess_and_add-contacts_tab_button_click';
import { useExperiment } from '@hh.ru/front-static-app';
import { Divider, Tab, Tabs, VSpacing } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import { TabProps } from 'bloko/blocks/tabs/Tab';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import translation from 'src/components/translation';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import { useIsHhru } from 'src/hooks/useSites';
import { PriceDataTab } from 'src/models/price/priceData';

const PRICE_TAB = 'price-tab';

const sendShownAnalytics = (tabs: string[], priceTabsRef: MutableRefObject<HTMLSpanElement[]>) => {
    tabs.forEach((tabName, index) => {
        if (priceTabsRef.current[index]) {
            Analytics.sendHHEventElementShown(priceTabsRef.current[index], {
                name: PRICE_TAB,
                tabName,
            });
        }
    });
};

// tempexp_33624_start
const GROUP_TABS_EXPERIMENT = ['dbaccess', 'add-contacts'];
const isExperimentGroupTabs = (tabName: string): tabName is 'dbaccess' | 'add-contacts' =>
    GROUP_TABS_EXPERIMENT.includes(tabName);
// tempexp_33624_end

const sendClickAnalytics = (
    tabName: string,
    // tempexp_33624_next-line
    tempexp33624?: boolean
) => {
    // tempexp_33624_start
    if (tempexp33624 && isExperimentGroupTabs(tabName)) {
        priceDbaccessAddContactsTabButtonClick({ buttonName: `price_${tabName}` });
        return;
    }
    // tempexp_33624_end

    Analytics.sendHHEventButtonClick(PRICE_TAB, {
        tabName,
    });
};

const TrlKeys = {
    [PriceDataTab.Recommended]: 'employer.price2.tabs.title.recommended',
    [PriceDataTab.PayForPerformance]: 'employer.price2.tabs.title.pay-for-performance',
    [PriceDataTab.Dbaccess]: 'employer.price2.tabs.title.dbaccess',
    [PriceDataTab.AddContacts]: 'employer.price2.tabs.title.add-contacts',
    [PriceDataTab.Publications]: 'employer.price2.tabs.title.publications',
    [PriceDataTab.RegionalPublications]: 'employer.price2.tabs.title.regional-publications',
    [PriceDataTab.Clickme]: 'employer.price2.tabs.title.clickme',
    [PriceDataTab.Branding]: 'employer.price2.tabs.title.branding',
    [PriceDataTab.EmployerBrand]: 'employer.price2.tabs.title.employerbrand',
    [PriceDataTab.ZpPublications]: 'employer.price2.tabs.title.zp-publications',
    [PriceDataTab.ZpResumeAccess]: 'employer.price2.tabs.title.zp-resume-access',
    [PriceDataTab.ZpBundles]: 'employer.price2.tabs.title.zp-bundles',
    [PriceDataTab.ZpPromotion]: 'employer.price2.tabs.title.zp-promotion',
    [PriceDataTab.ZpPriceBackOffice]: 'employer.price2.tabs.title.zp-price-backoffice',
    [PriceDataTab.ProfrolesPublications]: 'employer.price2.tabs.title.profroles-publications',
    // tempexp_33624_next-line
    [PriceDataTab.CRMSystem]: 'employer.price2.tabs.title.talantix.crm.system',
};

const PriceTabs: TranslatedComponent = ({ trls }) => {
    const { items, urls, activeTabName } = useSelectorNonNullable(({ priceData }) => priceData.priceTabs);
    const priceTabsRef = useRef<HTMLSpanElement[]>([]);
    let tabs = items.map((tabName, index) => (
        <Tab
            key={tabName}
            id={tabName}
            to={urls[tabName]}
            focused={tabName === activeTabName}
            Element={SPALink}
            data-qa={`cart__tab_${tabName}`}
            onClick={() => sendClickAnalytics(tabName)}
        >
            <span ref={(ref) => (ref ? (priceTabsRef.current[index] = ref) : null)}>{trls[TrlKeys[tabName]]}</span>
        </Tab>
    ));

    // tempexp_33624_start
    const isHhRu = useIsHhru();
    const [dividerProps, setDividerProps] = useState({ length: 0, marginLeft: 0 });
    const isExpirmentTalantixCrm = useExperiment(
        'exp_price_Talantix',
        !!items.filter(isExperimentGroupTabs).length && isHhRu
    );
    if (isExpirmentTalantixCrm) {
        let skip = false;
        tabs = items
            .map((tabName, index) => {
                if (isExperimentGroupTabs(tabName)) {
                    return { __type: 'replace', tabName, label: 'Резюме и контакты' };
                }
                const name = tabName === PriceDataTab.ProfrolesPublications ? 'Вакансии' : trls[TrlKeys[tabName]];
                return (
                    <Tab
                        key={tabName}
                        id={tabName}
                        to={urls[tabName]}
                        focused={tabName === activeTabName}
                        Element={SPALink}
                        data-qa={`cart__tab_${tabName}`}
                        onClick={() => sendClickAnalytics(tabName, isExpirmentTalantixCrm)}
                    >
                        <span ref={(ref) => (ref ? (priceTabsRef.current[index] = ref) : null)}>{name}</span>
                    </Tab>
                );
            })
            .reduce<JSX.Element[]>((newTabs, elementTab, index) => {
                if (typeof elementTab === 'object' && '__type' in elementTab && elementTab.__type === 'replace') {
                    if (!skip) {
                        const { tabName, label } = elementTab;
                        const id = activeTabName && isExperimentGroupTabs(activeTabName) ? activeTabName : tabName;
                        newTabs.push(
                            <Tab
                                key={id}
                                id={id}
                                to={urls[id]}
                                focused={activeTabName === id}
                                Element={SPALink}
                                data-qa={`cart__tab_${tabName}`}
                                onClick={() =>
                                    priceDbaccessAndAddContactsTabButtonClick({
                                        // @ts-expect-error требует None баг в генерации docs
                                        buttonName: 'price_dbaccess_and_add-contacts',
                                    })
                                }
                            >
                                <span ref={(ref) => (ref ? (priceTabsRef.current[index] = ref) : null)}>{label}</span>
                            </Tab>
                        );
                        skip = true;
                    }
                }
                if (isValidElement<ReactElement<TabProps>>(elementTab)) {
                    newTabs.push(elementTab);
                }
                return newTabs;
            }, []);
    }
    // tempexp_33624_end

    useEffect(() => {
        sendShownAnalytics(items, priceTabsRef);
    }, [items]);

    // tempexp_33624_start
    useEffect(() => {
        if (isExpirmentTalantixCrm) {
            const node = document.querySelector('.main-content .bloko-columns-wrapper');
            if (node) {
                const wrapper = getComputedStyle(node);
                const shift = parseInt(wrapper.marginLeft, 10) * 2;
                setDividerProps({ marginLeft: -shift, length: window.screen.width + shift });
            }
        }
    }, [isExpirmentTalantixCrm]);
    // tempexp_33624_end

    return (
        <>
            <Tabs activeItemId={activeTabName}>{tabs}</Tabs>
            <VSpacing default={40} />
            {/* tempexp_33624_start */}
            {isExpirmentTalantixCrm && isExperimentGroupTabs(activeTabName ?? '') && (
                <>
                    <Tabs activeItemId={activeTabName} mode="secondary">
                        {items.filter(isExperimentGroupTabs).map((tabName, index) => (
                            <Tab
                                id={tabName}
                                onClick={() => sendClickAnalytics(tabName, true)}
                                key={tabName}
                                Element={SPALink}
                                mode="secondary"
                                to={urls[tabName]}
                            >
                                <span ref={(ref) => (ref ? (priceTabsRef.current[index] = ref) : null)}>
                                    {trls[TrlKeys[tabName]]}
                                </span>
                            </Tab>
                        ))}
                    </Tabs>
                    <Divider {...dividerProps} />
                    <VSpacing default={40} />
                </>
            )}
            {/* tempexp_33624_end */}
        </>
    );
};

export default translation(PriceTabs);
